import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useCustomerProvider } from '@material-bank/customer';
import { shouldShowSimilarItems } from '@material-bank/products-utils';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { setCurrentProject as setApiCurrentProject } from '@mb/api';
import {
  CustomEventName,
  shouldTriggerProjectSelectionDialog,
  AnalyticsEventLocation,
  getAnalyticsItemType,
  getAnalyticsItemCategoriesByTaxonomy,
  isEuEnv,
  getUserLang,
} from '@mb/lib';
import { isCustomerFromGensler } from '@mb/lib/customer';
import { SnackbarStatusEnum } from '@mb/types';
import { Box, Typography } from '@mui/material';
import { client } from '@productSearch/api/http';
import { config } from '@productSearch/config';
import { definedTranslation as catalogTranslation } from '@productSearch/i18n';
import {
  getRecommendations,
  type RecommendedItemType,
} from '@recommendation/app/api/recommendations';
import { OpenRecomendationsModalPayload } from '@recommendation/app/types/recommendationsModal.types';
import { TopInfoItem } from 'components/Cards/ProductCard/ProductCard.types';
import { useIntl } from 'react-intl';
import { VirtuosoGrid } from 'react-virtuoso';
import palette from 'theme/palette';
import { MBSnackbar, ProductCard } from 'ui-widgets';
import {
  CUSTOM_CLOSE_RESTRICTED_ITEM_MODAL,
  CUSTOM_OPEN_RESTRICTED_ITEM_MODAL,
  RestrictedModalTypesEnum,
} from './const';
import { sendAnalytics } from './events';
import { definedTranslation } from './i18n';
import * as Styled from './RestrictedItemsModal.styled';
const isGenslerUser = isCustomerFromGensler();

const RestrictedItemModal = () => {
  const [modalData, setModalData] = useState<RestrictedModalData | null>(null);
  const [loadingItems, setLoadingItems] = useState<string[]>([]);
  const [cartItemIds, setCartItemIds] = useState<string[]>([]);
  const [snackbarInfo, setSnackbarInfo] = useState({
    isOpen: false,
    message: '',
    status: SnackbarStatusEnum.DEFAULT,
  });
  const [recommendXHeader, setRecommendXHeader] = useState<string | undefined>(
    undefined,
  );

  const { activeProjects } = useProjectsProvider();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { customer } = useCustomerProvider();
  const { isProjectDataComplete } = useValidateProjectData();

  const urlPrefix = isEuEnv ? `/${getUserLang()}` : '';

  const currentProjectData = currentProject?.externalId
    ? {
        id: currentProject?.externalId,
        label: currentProject?.title ?? 'Select project..',
      }
    : null;
  const intl = useIntl();
  const structuredProjects = useMemo(() => {
    return Object.values(activeProjects).map((project) => ({
      id: project.externalId,
      label: project.title,
    }));
  }, [activeProjects]);
  const items = modalData?.suggestedItems || [];
  const itemsIds = items?.map((item) => String(item?.entity_id));
  const scrollerRef = useRef<HTMLDivElement>(null);

  const restrictionType: RestrictedModalTypesEnum =
    modalData?.restrictedItem?.restrictionType ||
    RestrictedModalTypesEnum.UNAVAILABLE;

  const labels = {
    title: intl.formatMessage(definedTranslation[restrictionType].message),
    sidebar: intl.formatMessage(definedTranslation[restrictionType].reason),
    projectUpdated: intl.formatMessage(
      catalogTranslation.projectMessages.projectUpdated,
    ),
    addedToCart: intl.formatMessage(catalogTranslation.card.addedToCartLabel),
  };

  const cardLabels = {
    addToCartLabel: intl?.formatMessage(catalogTranslation.card.addToCartLabel),
    addedToCartLabel: intl?.formatMessage(
      catalogTranslation.card.addedToCartLabel,
    ),
    createProjectButtonLabel: intl?.formatMessage(
      catalogTranslation.card.createProjectButtonLabel,
    ),
    inStockLabel: intl?.formatMessage(catalogTranslation.card.inStockLabel),
    outOfStockLabel: intl?.formatMessage(
      catalogTranslation.card.outOfStockLabel,
    ),
    projectPreLabel: intl?.formatMessage(
      catalogTranslation.card.projectPreLabel,
    ),
    saveButtonLabel: intl?.formatMessage(
      catalogTranslation.card.saveButtonLabel,
    ),
    savedButtonLabel: intl?.formatMessage(
      catalogTranslation.card.savedButtonLabel,
    ),
    savingButtonLabel: intl?.formatMessage(
      catalogTranslation.card.savingButtonLabel,
    ),
    searchPlaceholderLabel: intl?.formatMessage(
      catalogTranslation.filters.searchPlaceholder,
    ),
  };

  useEffect(() => {
    const openEventListener = async (e: CustomEvent<RestrictedItemDetails>) => {
      const restrictedItem = e.detail;

      let additionalParams = {};

      if (restrictedItem?.material?.isGpsCompliant && isGenslerUser) {
        additionalParams = {
          filters: [
            {
              field: 'gps_eligible',
              value: ['Yes'],
            },
          ],
        };
      }

      const { recommendedItems = [], xHeader } = await getRecommendations(
        [String(restrictedItem?.entityId)],
        'id',
        true,
        6,
        'geo',
        additionalParams,
        currentProject,
      );
      // @ts-expect-error // TODO: fix types
      setRecommendXHeader(xHeader);
      const manufacturerSku =
        typeof restrictedItem.material?.manufacturer_sku === 'string'
          ? restrictedItem.material?.manufacturer_sku
          : restrictedItem.material?.manufacturer_sku?.[0];
      const customer = JSON.parse(
        localStorage.getItem('mage-cache-storage') || '{}',
      )?.customer;

      addRestrictedItem(
        restrictedItem.material.sku,
        manufacturerSku || '',
        restrictedItem.material.manufacturer,
        customer?.region_id,
        customer?.country,
        customer?.customer_id,
        restrictedItem.projectId,
      );

      setModalData({
        restrictedItem,
        suggestedItems: recommendedItems,
      });
    };

    window.addEventListener(
      CUSTOM_OPEN_RESTRICTED_ITEM_MODAL,
      openEventListener,
    );
    window.addEventListener(
      CUSTOM_CLOSE_RESTRICTED_ITEM_MODAL,
      closeEventListener,
    );

    return () => {
      window.removeEventListener(
        CUSTOM_OPEN_RESTRICTED_ITEM_MODAL,
        openEventListener,
      );
      window.addEventListener(
        CUSTOM_CLOSE_RESTRICTED_ITEM_MODAL,
        closeEventListener,
      );
    };
  }, [currentProject]);

  const closeEventListener = () => {
    setModalData(null);
  };

  const onProjectSelect = useCallback(
    (projectId: number) => {
      setCurrentProjectId(projectId.toString());
    },
    [setCurrentProjectId],
  );

  const handleAddToCartFlow = async (
    id: RecommendedItemType['entity_id'],
    material: RecommendedItemType,
    project: any,
  ) => {
    const result = await addToCart(
      [{ id, itemListId: 'geo_sim', itemListName: 'Geo sim' }],
      project,
    );
    await sendAnalytics(
      {
        id: [String(id)],
        type: 'cart',
        requestId: recommendXHeader || '',
        impressions: itemsIds,
      },
      1200,
    );
    const controlRuleId = result?.modal_data?.[0]?.control_rule_id ?? null;
    setLoadingItems((prev) =>
      prev.filter((prevId) => String(prevId) !== String(id)),
    );

    if (result !== null) {
      if (result?.message) {
        const guardrailsDailyLimitReached =
          result.modal_data?.[0]?.guardrails_daily_limit_reached ?? false;

        // SIM modal logic:
        // - For Gensler users: only show if product is NOT GPS compliant
        // - For all other users: always show (existing logic)
        const shouldAllowRecommendationsModal = shouldShowSimilarItems({
          isGpsCompliant: !!material?.is_gps_compliant,
          isGenslerCustomer: !!customer?.isGenslerCustomer,
        });

        window.dispatchEvent(
          new CustomEvent<OpenRecomendationsModalPayload>(
            'open-recommendations-modal',
            {
              detail: {
                forceAllow: true,
                inProgress: false,
                shouldShowProposal: false,
                serverMessage: result?.message,
                skus: [String(id)],
                isInRealTimeStock: false,
                materialId: String(id),
                bagProject: null,
                addedMaterialImage: material?.thumbnailImageUrl ?? null,
                controlRuleId,
                manufacturer: material?.manufacturer,
                guardrailsDailyLimitReached,
                shouldHideRecommendations: !shouldAllowRecommendationsModal,
              },
            },
          ),
        );

        return;
      }

      setCartItemIds((prev) => [...prev, String(id)]);
      setTimeout(() => {
        setCartItemIds((prev) =>
          prev.filter((val) => String(val) !== String(id)),
        );
      }, 3500);

      const currency = isEuEnv ? 'EUR' : 'USD';

      const geoSimAtcEvent = {
        event: 'add_to_cart',
        event_location: 'Geo sim',
        list_id: 'geo_sim',
        list_name: 'Geo sim',
        method: 'Single',
        ecommerce: {
          currency,
          value: 0,
          items: [
            {
              item_id: material.sku,
              item_name: material.name,
              item_brand: material.manufacturer,
              item_type: getAnalyticsItemType(material),
              ...getAnalyticsItemCategoriesByTaxonomy(material.taxonomy ?? []),
              item_category4: material.industry_sector?.join(', ') || '',
              item_category5: project?.type?.label || '',
              item_manufacturer_sample_id: material.manufacturer_sample_id,
              family_sku: material.family_sku,
              index: 1,
              price: 0,
              quantity: 1,
              item_list_id: 'geo_sim',
              item_list_name: 'Geo sim',
              item_variant: '',
            },
          ],
        },
      };

      mitGTM.sendEvent({ ecommerce: null });
      mitGTM.sendEvent(geoSimAtcEvent);

      setSnackbarInfo({
        isOpen: true,
        message: labels.addedToCart,
        status: SnackbarStatusEnum.DEFAULT,
      });

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('reload-customer-section-data'));
      }, 500);
    } else {
      setSnackbarInfo({
        isOpen: true,
        message: 'Some error occurred',
        status: SnackbarStatusEnum.DANGER,
      });
    }
  };

  const handleAddToCart = async (
    material: RecommendedItemType,
    isAddToCartLoading = false,
  ) => {
    const id = material?.entity_id;

    if (isAddToCartLoading) {
      return;
    }

    if (!currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: AnalyticsEventLocation.CATALOG,
            successCallback: (projectData: any) => {
              handleAddToCartFlow(id, material, projectData);
            },
          },
        }),
      );

      return;
    }

    if (shouldTriggerProjectSelectionDialog()) {
      window.dispatchEvent(
        new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
          detail: {
            currentProjectId: currentProjectData?.id,
            analyticsEventLocation: AnalyticsEventLocation.CATALOG,
            onCurrentProjectSave: (project: any) => {
              setLoadingItems((prev) => [...prev, String(id)]);

              const projectId = project?.externalId || project?.project_id;
              setCurrentProjectId(projectId);

              handleAddToCartFlow(id, material, project);
            },
          },
        }),
      );

      return;
    }

    // validate project data
    if (!isProjectDataComplete(currentProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: currentProject.externalId,
        successCallback: async (project: ProjectModel) => {
          handleAddToCartFlow(id, material, project);
        },
      });

      return;
    }

    setLoadingItems((prev) => [...prev, String(id)]);

    handleAddToCartFlow(id, material, currentProject);
  };

  const handleProjectClick = (projectId: number) => {
    const project = activeProjects[projectId];

    if (project) {
      if (!isProjectDataComplete(project)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: project.externalId,
          successCallback: async (project: ProjectModel) => {
            onProjectSelect(project.externalId);
          },
        });
      } else {
        setApiCurrentProject(project.externalId).then(() => {
          setSnackbarInfo({
            isOpen: true,
            message: labels.projectUpdated,
            status: SnackbarStatusEnum.SUCCESS,
          });

          setCurrentProjectId(project.externalId);

          window.dispatchEvent(
            new CustomEvent('project-selected', {
              detail: {
                project: {
                  project_id: project.externalId,
                },
              },
            }),
          );
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarInfo({
      isOpen: false,
      message: '',
      status: SnackbarStatusEnum.DEFAULT,
    });
  };

  const handleProductClick = async (url: string, id: number) => {
    await sendAnalytics(
      {
        id: [String(id)],
        type: 'view',
        requestId: recommendXHeader || '',
        impressions: itemsIds,
      },
      1200,
    );
    window.location.href = url;
  };

  const renderItem = (index: number) => {
    const material = items[index];

    if (material == null) {
      return null;
    }
    const materialUrlWithPrefix = `${urlPrefix}${material?.url}`;
    const isAddToCartLoading = loadingItems.includes(
      String(material?.entity_id),
    );
    const isInCart = cartItemIds.includes(String(material?.entity_id));
    const availableSubcategories: string[] =
      material.categories?.filter((item) => item.includes('>')) ?? [];
    const firstSubcategory = availableSubcategories?.[0]?.split('>')?.pop();
    const lastSubcategory = availableSubcategories?.[
      availableSubcategories.length - 1
    ]
      ?.split('>')
      ?.pop();
    const subcategoryToShow = material.categories?.includes(
      firstSubcategory ?? '',
    )
      ? lastSubcategory
      : firstSubcategory;

    const shouldShowGpsLabel = material.is_gps_compliant && isGenslerUser;

    const topLabels: TopInfoItem[] = shouldShowGpsLabel
      ? [
          {
            label: 'GPS',
            iconName: 'check',
            bgColor: palette.secondary?.main,
            textColor: '#e82011', // Gensler brand color
          },
        ]
      : [];

    return (
      <Box margin="8px">
        <ProductCard
          onProjectClick={handleProjectClick}
          selectedProject={currentProjectData}
          onProductClick={() =>
            handleProductClick(material?.url, material?.entity_id)
          }
          isOvernight
          topInfoItems={topLabels}
          isAddedToCart={isInCart}
          entityId={material?.entity_id}
          title={material?.manufacturer}
          productUrl={materialUrlWithPrefix}
          showContainedInfo={false}
          showAdvancedInfo
          centerAdvancedInfo
          subtitle1={subcategoryToShow}
          subtitle2={material?.name}
          showExtraInfo={false}
          showProjectsDropdown
          projects={structuredProjects}
          isAddToCartLoading={isAddToCartLoading}
          onAddToBag={() => handleAddToCart(material, isAddToCartLoading)}
          onCreateProject={() => {
            window.dispatchEvent(new CustomEvent('open-create-project-modal'));
          }}
          showHoverFeatures={false}
          showFeatures={false}
          isHoverAllowed
          imageUrl={material?.thumbnailImageUrl}
          {...cardLabels}
        />
      </Box>
    );
  };
  if (!modalData) {
    return null;
  }

  return (
    <>
      <Styled.ModalWrapper ref={scrollerRef}>
        <Styled.Dialog
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          disableRestoreFocus
          id="RestrictedItemModal"
          open={!!modalData}
          isPalette={false}
          onClose={closeEventListener}
        >
          <Styled.CloseIcon
            iconName="close"
            onClick={closeEventListener}
            size="medium"
            dense
          />
          <Styled.ContentContainer>
            <Styled.Sidebar>
              <Box width={173} mt="75px">
                <ProductCard
                  selectedProject={currentProjectData}
                  hideActionButton
                  entityId={modalData.restrictedItem?.entityId}
                  onCreateProject={() => {
                    window.dispatchEvent(
                      new CustomEvent('open-create-project-modal'),
                    );
                  }}
                  outOfStockLabel={cardLabels.outOfStockLabel}
                  title={modalData.restrictedItem?.title}
                  showContainedInfo={false}
                  showAdvancedInfo
                  centerAdvancedInfo
                  subtitle1={modalData.restrictedItem?.subtitle1}
                  subtitle2={modalData.restrictedItem?.subtitle2}
                  showExtraInfo={false}
                  showProjectsDropdown={false}
                  showHoverFeatures={false}
                  showFeatures={false}
                  isHoverAllowed={false}
                  imageUrl={modalData.restrictedItem?.imgUrl}
                />
              </Box>
              <Typography>{labels.sidebar}</Typography>
            </Styled.Sidebar>
            <Styled.MainContent>
              <Styled.ModalTitle>
                <Typography color="primary" variant="h4" fontWeight={700}>
                  {labels.title}
                </Typography>
              </Styled.ModalTitle>
              <Styled.FinishesList>
                <VirtuosoGrid
                  useWindowScroll
                  // @ts-expect-error // TODO: fix types
                  customScrollParent={scrollerRef?.current}
                  overscan={300}
                  totalCount={items.length}
                  style={{ height: window.innerHeight }}
                  components={{
                    Item: Styled.ItemContainer,
                  }}
                  itemContent={renderItem}
                />
              </Styled.FinishesList>
            </Styled.MainContent>
          </Styled.ContentContainer>
        </Styled.Dialog>
      </Styled.ModalWrapper>
      {/* @ts-expect-error // TODO: fix types */}
      <MBSnackbar
        open={snackbarInfo?.isOpen}
        autoHideDuration={2500}
        onClose={handleCloseSnackbar}
        {...snackbarInfo}
      />
    </>
  );
};

////

export type RestrictedItemDetails = {
  title: string;
  subtitle1: string;
  subtitle2: string;
  imgUrl: string;
  entityId: string;
  projectId: string | number;
  restrictionType: RestrictedModalTypesEnum;
  material: {
    sku: string;
    manufacturer: string;
    manufacturer_sku?: string[] | string;
    isGpsCompliant?: boolean;
  };
};

export type RestrictedModalData = {
  restrictedItem: RestrictedItemDetails;
  suggestedItems: RecommendedItemType[];
};

type AddRestrictedItemPayload = {
  sku: string;
  manufacturer_sku?: string;
  manufacturer_name?: string;
  region_code?: string;
  country_id?: string;
  customer_id?: string;
  project_id?: string | number;
};

export const addRestrictedItem = async (
  sku: string,
  manufacturerSku: string,
  manufacturerName: string,
  regionCode: string,
  countryId: string,
  customerId?: string,
  projectId?: string | number,
) => {
  const restrictedItem: AddRestrictedItemPayload = {
    sku: sku,
  };

  if (manufacturerSku) {
    restrictedItem['manufacturer_sku'] = manufacturerSku;
  }

  if (manufacturerName) {
    restrictedItem['manufacturer_name'] = manufacturerName;
  }

  if (regionCode) {
    restrictedItem['region_code'] = regionCode;
  }

  if (countryId) {
    restrictedItem['country_id'] = countryId;
  }

  if (customerId) {
    restrictedItem['customer_id'] = customerId;
  }

  if (projectId) {
    restrictedItem['project_id'] = projectId;
  }

  const result = await client.post(config.endpoints.addRestrictedItem, {
    restrictedItem: restrictedItem,
  });

  return result;
};

export const addToCart = async (
  materials: Record<string, any>,
  bagProject: any,
) => {
  try {
    const params = new URLSearchParams();

    const projectId =
      bagProject?.externalId?.toString() ?? bagProject?.project_id ?? '';

    const projectTitle = bagProject?.title ?? bagProject?.project_title ?? '';

    const projectType =
      bagProject?.type?.externalId?.toString() ??
      bagProject?.project_type ??
      '';

    materials.forEach((item: Record<string, any>, index: number) => {
      params.append(`product_id[${index}]`, item.id.toString());
      params.append(`qty[${index}]`, '1');
      params.append(`project_name[${index}]`, projectTitle);
      params.append(`project_type[${index}]`, projectType);
      params.append(`project_id[${index}]`, projectId);

      if (item?.itemListId != null) {
        params.append(`item_list_id[${index}]`, item?.itemListId);
      }

      if (item?.itemListName != null) {
        params.append(`item_list_name[${index}]`, item?.itemListName);
      }

      if (item?.is_diverzify != null) {
        params.append(`is_diverzify[${index}]`, item?.is_diverzify);
      }

      params.append(`index[${index}]`, String(item?.index || index + 1));
    });

    const { data } = await client.post(config.endpoints.addToBag, params);

    return data;
  } catch {
    return null;
  }
};

export default RestrictedItemModal;
